import React, { useEffect, useState, useCallback } from 'react';
import { toast } from './ui/use-toast';

import { API_BASE_URL, CLOUDFRONT_DOMAIN } from '../config';
import { FUNCTION_NAMES, BUCKET_NAME_PHOTOS } from '../constants';
import { Card, CardContent, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Check, Trash2, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { extractTimestamp, getImageUrls } from '../utils/imageUtils';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { useUser } from './UserContext';
import { Modal } from './ui/modal';
import { useViewerState } from './useViewerState';
import UserSelector from './UserSelector';
import LocationSelector from './LocationSelector';
import { fetchWithRetry, loadingSpinner } from '../utils/tourUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import ContractorSelector from './ContractorSelector';
import LocationSelectorWithCache from './LocationSelectorWithCache'; // ✅ Import it
import ConfirmDialog from './ConfirmDialog';

const ImageSelector = () => {
  const [localSelectedUser, setLocalSelectedUser] = useState('');
  const { selectedUser: globalSelectedUser } = useUser(); // still access global if needed

  const [tourName, setTourName] = useState('');
  const [allImageKeys, setAllImageKeys] = useState([]);
  const [thumbnailsUrls, setThumbnailsUrls] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { viewerState, pannellumRef, handleMouseDown, handleMouseUp, handleRoomChange } =
    useViewerState();
  const { selectedUser, selectedUserEmail } = useUser();
  const [assignedImageKeys, setAssignedImageKeys] = useState([]);
  const [spaceResetKey, setSpaceResetKey] = useState(0);
  const [isLoadingThumbnails, setIsLoadingThumbnails] = useState(false);

  // Location state (managed by LocationSelector)
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedLot, setSelectedLot] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedFloor, setSelectedFloor] = useState('');
  const [selectedSpace, setSelectedSpace] = useState('');
  const [isReadyToFilter, setIsReadyToFilter] = useState(false);
  const [didLoadImageKeys, setDidLoadImageKeys] = useState(false);
  const [didLoadAssignedKeys, setDidLoadAssignedKeys] = useState(false);
  const [tourType, setTourType] = useState('');
  const [contractorId, setContractorId] = useState('');

  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    imageKey: null,
    isProcessing: false,
  });

  const [showAllUsers, setShowAllUsers] = useState(false);

  const fetchAssignedTourImages = async () => {
    const result = await fetchWithRetry({
      url: `${API_BASE_URL}/api/post`,
      data: {
        function_name: FUNCTION_NAMES.PRISMA,
        operation: 'tourImage.findMany',
        payload: {
          select: {
            imageKey: true,
          },
        },
      },
      label: 'assigned tour images',
    });

    const keys = result.map(item => item.imageKey);
    setAssignedImageKeys(keys);
    setDidLoadAssignedKeys(true); // ✅
  };
  useEffect(() => {
    fetchAssignedTourImages();
  }, []);

  useEffect(() => {
    const fetchImageKeys = async () => {
      if (allImageKeys.length > 0) return;

      const result = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.POST.LIST_S3_KEYS,
          payload: {
            bucketName: BUCKET_NAME_PHOTOS,
            prefixPath: 'raw/images/',
          },
        },
        label: 'image keys from S3',
      });
      console.log(allImageKeys, 'allimageskeys');
      setAllImageKeys(result);
      setDidLoadImageKeys(true); // ✅
    };

    fetchImageKeys();
  }, [allImageKeys]);
  useEffect(() => {
    if (didLoadImageKeys && didLoadAssignedKeys) {
      setIsReadyToFilter(true);
    }
  }, [didLoadImageKeys, didLoadAssignedKeys]);

  const filteredImageKeys = React.useMemo(() => {
    if (!localSelectedUser || !isReadyToFilter) return []; // <== fix here

    return allImageKeys
      .filter(item => {
        const isFromUser = item.Key.startsWith(`raw/images/${localSelectedUser}/`);
        const isNotAssigned = !assignedImageKeys.includes(item.Key);
        return isFromUser && isNotAssigned;
      })
      .map(item => {
        const fileName = item.Key.split('/').pop();
        const { date, time } = extractTimestamp(fileName);
        return {
          ...item,
          timestamp: { date, time },
          dateString: `${date.slice(6)}-${date.slice(3, 5)}-${date.slice(0, 2)}T${time}`,
        };
      });
  }, [localSelectedUser, isReadyToFilter, allImageKeys, assignedImageKeys]);

  const setThumbnails = useCallback(async thumbnailsImageKeys => {
    setIsLoadingThumbnails(true);

    try {
      const thumbnailsUrlsFromS3 = await getImageUrls(thumbnailsImageKeys);
      const thumbnailMap = {};
      thumbnailsUrlsFromS3.forEach(({ key, url }) => {
        const originalKey = key.replace('/thumbnails/', '/images/');
        thumbnailMap[originalKey] = url;
      });
      setThumbnailsUrls(thumbnailMap); // now an object
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load thumbnails', variant: 'destructive' });
    } finally {
      setIsLoadingThumbnails(false);
    }
  }, []);

  useEffect(() => {
    if (filteredImageKeys.length > 0) {
      const thumbnailsImageKeys = filteredImageKeys.map(item =>
        item.Key.replace('/images/', '/thumbnails/')
      );
      setThumbnails(thumbnailsImageKeys);
    }
  }, [filteredImageKeys, setThumbnails]);

  const handleImageSelect = image => {
    const imageKey = image.Key || image; // handle string or object
    setSelectedImages(prev =>
      prev.includes(imageKey) ? prev.filter(key => key !== imageKey) : [...prev, imageKey]
    );
  };

  const handleImageClick = async imageKey => {
    toast({ title: 'info', description: 'loading 360 please wait' });
    const imageUrls = await getImageUrls([imageKey]);
    setSelectedImageUrl(imageUrls[0].url);
    setModalOpen(true);
  };

  const handleAssignImages = async () => {
    // debugger;
    if (!selectedSpace || selectedImages.length === 0 || !tourName) {
      toast({
        title: 'Error',
        description: 'Please select a space, enter a tour name, and select images',
        variant: 'destructive',
      });
      return;
    }
    const tourData = {
      createdBy: selectedUser,
      imageKeys: selectedImages,
      name: tourName,
      isActivePublic: true,
      projectId: selectedProject,
      contractorId,
      lotId: selectedLot,
      buildingId: selectedBuilding,
      floorId: selectedFloor,
      spaceId: selectedSpace,
      tourType, // 🛠 add tourType here
    };
    let tour;
    try {
      tour = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.POST.SAVE_TOUR,
          payload: {
            contractorId,
            tourData,
          },
        },
        label: 'save tour',
      });
      debugger;
      toast({ title: 'Success', description: 'Images assigned to tour successfully' });

      try {
        await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.POST.PUSH_TO_PRIORITY,
            payload: {
              spaceId: parseInt(selectedSpace),
              publicLink: `${CLOUDFRONT_DOMAIN}/public-tour/${tour.id}`,
              contractorId: tourData.contractorId,
            },
          },
          label: 'push to priority',
        });
        toast({ title: 'Success', description: 'PUSH_TO_PRIORITY successfully' });
      } catch (e) {
        toast({
          title: 'PUSH_TO_PRIORITY',
          description: `PUSH_TO_PRIORITY failed.`,
          variant: 'destructive',
        });
      }

      setSelectedImages([]);
      // Only reset the space field, keep other location selections
      setSelectedSpace('');
      setSpaceResetKey(prev => prev + 1); // ⬅️ resets only space dropdown
      window.open(`${window.location.origin}/tour-editor/${tour.id}`, '_blank');

      await fetchAssignedTourImages();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to assign images', variant: 'destructive' });
    }
  };
  const distinctUsers = React.useMemo(() => {
    const userIds = new Set();

    allImageKeys.forEach(item => {
      const parts = item.Key.split('/');
      if (parts.length > 2) {
        const userId = parts[2]; // raw/images/{userId}/filename
        userIds.add(userId);
      }
    });

    return Array.from(userIds);
  }, [allImageKeys]);

  const handleDeleteImage = (imageKey, event) => {
    // Stop propagation to prevent other click handlers
    event.stopPropagation();

    setDeleteDialog({
      isOpen: true,
      imageKey,
      isProcessing: false,
    });
  };

  const confirmDeleteImage = async () => {
    const { imageKey } = deleteDialog;

    setDeleteDialog(prev => ({ ...prev, isProcessing: true }));

    try {
      await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.POST.DELETE_S3_KEY,
          payload: {
            key: imageKey,
            targetBucket: BUCKET_NAME_PHOTOS,
          },
        },
        label: 'delete_image',
      });

      // Remove image from filtered keys
      setAllImageKeys(prev => prev.filter(item => item.Key !== imageKey));

      // Remove from selected images if present
      setSelectedImages(prev => prev.filter(key => key !== imageKey));

      toast({
        title: 'Success',
        description: 'Image deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete image',
        variant: 'destructive',
      });
    } finally {
      setDeleteDialog({
        isOpen: false,
        imageKey: null,
        isProcessing: false,
      });
    }
  };

  const cancelDeleteImage = () => {
    setDeleteDialog({
      isOpen: false,
      imageKey: null,
      isProcessing: false,
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={cancelDeleteImage}
        onConfirm={confirmDeleteImage}
        title="Delete Image"
        message="Are you sure you want to delete this image? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isProcessing={deleteDialog.isProcessing}
        processingText="Deleting..."
      />

      {/* Header with User Selection - Sticky */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
        <div className="p-4 space-y-3">
          {/* User Selection Bar */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              <h3 className="font-semibold text-gray-800 whitespace-nowrap">Select User:</h3>
              <div className="flex-1 max-w-xs">
                <UserSelector 
                  selectedUser={localSelectedUser} 
                  setSelectedUser={setLocalSelectedUser} 
                />
              </div>
              {localSelectedUser && (
                <div className="text-sm text-blue-600 font-medium px-3 py-1 bg-blue-50 rounded-full">
                  ✓ {localSelectedUser}
                </div>
              )}
            </div>
            {!localSelectedUser && (
              <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                👆 Please select a user to view images
              </div>
            )}
          </div>

          {/* Stats Bar */}
          <div className="flex items-center justify-between text-sm text-gray-600 pt-2 border-t border-gray-100">
            <div className="flex items-center gap-6">
              <span>Total Images: <span className="font-medium">{allImageKeys.length}</span></span>
              {distinctUsers.length > 0 && (
                <div className="flex items-center gap-2">
                  <span>Available Users: <span className="font-medium">{distinctUsers.length}</span></span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    onClick={() => setShowAllUsers(!showAllUsers)}
                  >
                    <Users className="h-3 w-3 mr-1" />
                    {showAllUsers ? 'Hide' : 'Show All'}
                    {showAllUsers ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                  </Button>
                </div>
              )}
            </div>
            {localSelectedUser && (
              <div className="flex items-center gap-4">
                <span>Available: <span className="font-medium">{filteredImageKeys.length}</span></span>
                {selectedImages.length > 0 && (
                  <span className="text-green-600">
                    Selected: <span className="font-medium">{selectedImages.length}</span>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* All Users Expandable Section */}
          {showAllUsers && distinctUsers.length > 0 && (
            <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg max-h-96">
              <div className="text-xs text-gray-500 mb-2 font-medium">Click to select user:</div>
              <ScrollArea className="h-full max-h-80">
                <div className="flex flex-wrap gap-2 pr-4">
                  {distinctUsers.map(userId => (
                    <Button
                      key={userId}
                      variant={localSelectedUser === userId ? "default" : "outline"}
                      size="sm"
                      className={`text-xs h-7 ${
                        localSelectedUser === userId 
                          ? 'bg-blue-600 text-white' 
                          : 'hover:bg-blue-50 hover:border-blue-300'
                      }`}
                      onClick={() => {
                        setLocalSelectedUser(userId);
                        setShowAllUsers(false); // Auto-collapse after selection
                      }}
                    >
                      {localSelectedUser === userId && <Check className="h-3 w-3 mr-1" />}
                      {userId}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
              <div className="text-xs text-gray-400 mt-2">
                Users with uploaded images: {distinctUsers.length} total
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Three Panel Layout */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        
        {/* Left Panel - Location Selection */}
        <div className="w-full lg:w-80 border-b lg:border-b-0 lg:border-r border-gray-200 bg-gray-50 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800">📍 Location Selection</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              <LocationSelectorWithCache
                hideFloorSpace={false}
                spaceResetKey={spaceResetKey}
                onChange={({
                  selectedProject,
                  selectedLot,
                  selectedBuilding,
                  selectedFloor,
                  selectedSpace,
                }) => {
                  setSelectedProject(selectedProject);
                  setSelectedLot(selectedLot);
                  setSelectedBuilding(selectedBuilding);
                  setSelectedFloor(selectedFloor);
                  setSelectedSpace(selectedSpace);

                  const newTourName = [
                    selectedProject,
                    selectedLot,
                    selectedBuilding,
                    selectedFloor,
                    selectedSpace,
                  ]
                    .filter(Boolean)
                    .join('_');
                  setTourName(newTourName);
                }}
              />
              
              {selectedSpace && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-sm text-green-800">
                    <div className="font-medium">✓ Space Selected</div>
                    <div className="text-xs mt-1 opacity-75">Ready to create tour</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Middle Panel - Images */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="p-4 border-b border-gray-200 bg-white">
            {filteredImageKeys.length == 0 ? (
              <div className="text-center py-8 text-gray-500">
                {localSelectedUser ? 'No Images Found for Selected User' : 'Please select a user to view images'}
              </div>
            ) : (
              <h3 className="font-semibold text-gray-800">
                Select Images ({filteredImageKeys.length} available)
              </h3>
            )}
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            {isLoadingThumbnails ? (
              <div className="flex justify-center items-center h-64">
                {loadingSpinner()}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-4 pb-4">
                {filteredImageKeys.map((image, i) => (
                  <Card key={image.Key} className="relative overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="p-2 bg-gray-100 text-gray-700 text-xs font-medium text-center border-b">
                      {image.timestamp
                        ? `${image.timestamp.date} ${image.timestamp.time}`
                        : 'No Date'}
                    </div>
                    <CardContent className="p-0">
                      <div className="relative">
                        <img
                          src={thumbnailsUrls[image.Key]}
                          alt={image.Key}
                          className="w-full h-32 object-cover"
                        />

                        <div className="absolute top-2 right-2">
                          <Button
                            size="sm"
                            variant={selectedImages.includes(image.Key) ? 'default' : 'outline'}
                            className={
                              selectedImages.includes(image.Key)
                                ? 'bg-green-500 hover:bg-green-600 text-white h-8 w-8 p-0'
                                : 'h-8 w-8 p-0 bg-white/90 hover:bg-white'
                            }
                            onClick={() => handleImageSelect(image)}
                          >
                            <Check
                              className={`h-4 w-4 ${selectedImages.includes(image.Key) ? '' : 'opacity-50'}`}
                            />
                          </Button>
                        </div>

                        <div className="absolute top-2 left-2">
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-8 w-8 p-0 bg-red-500/90 hover:bg-red-600"
                            onClick={e => handleDeleteImage(image.Key, e)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>

                      <div className="p-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          className="w-full text-xs"
                          onClick={() => handleImageClick(image.Key)}
                        >
                          360 View
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Tour Creation & Selected Images */}
        <div className="w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-gray-200 bg-gray-50 flex flex-col">
          
          {/* Tour Creation Form */}
          <div className="border-b border-gray-200 bg-white">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800">Create Tour</h3>
            </div>
            <div className="max-h-80 overflow-y-auto p-4">
              <div className="space-y-3">
                <div>
                  <Label htmlFor="tourType" className="text-sm">Tour Type</Label>
                  <Select value={tourType} onValueChange={setTourType}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="בחר סוג סיור" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <ScrollArea className="h-full">
                        <SelectItem value="TRUM_TICHNON">תרום תכנון</SelectItem>
                        <SelectItem value="TICHNON_VS_BITZUA">תכנון מול ביצוע</SelectItem>
                        <SelectItem value="TIUD_MESIRA">תיעוד מסירה</SelectItem>
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="contractorId" className="text-sm">בחר קבלן</Label>
                  <div className="mt-1">
                    <ContractorSelector 
                      value={contractorId} 
                      onChange={setContractorId} 
                      hideAdd={true} 
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="tourName" className="text-sm">Tour Name</Label>
                  <Input
                    id="tourName"
                    value={tourName}
                    onChange={e => setTourName(e.target.value)}
                    placeholder="Enter tour name"
                    className="mt-1"
                  />
                </div>

                <Button
                  className="w-full"
                  onClick={handleAssignImages}
                  disabled={
                    !tourName || !tourType || !contractorId || selectedImages.length === 0 || !selectedSpace
                  }
                >
                  Create Tour ({selectedImages.length} images)
                </Button>
              </div>
            </div>
          </div>

          {/* Selected Images List */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="p-4 border-b border-gray-200">
              <h4 className="font-medium text-gray-800">
                Selected Images ({selectedImages.length})
              </h4>
            </div>
            
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-3">
                {selectedImages.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    No images selected yet
                  </div>
                ) : (
                  selectedImages.map((imageKey, i) => (
                    <Card key={imageKey} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <img
                            src={thumbnailsUrls[imageKey]}
                            alt={imageKey}
                            className="w-12 h-12 object-cover rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-gray-600 truncate">
                              #{i + 1}
                            </div>
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleImageSelect(imageKey)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        contentType="pannellum"
        contentProps={{
          pannellumProps: {
            pannellumRef,
            hotspotDebug: true,
            mediaPhoto: selectedImageUrl,
            pitch: viewerState.pitch,
            yaw: viewerState.yaw,
            hfov: viewerState.hfov,
            startPoints: viewerState.startPoints || {},
            hotspots: viewerState.hotspots || {},
            currentRoom: viewerState.currentRoom,
            rooms: viewerState.rooms || [],
            handleRoomChange,
            handleMouseDown,
            handleMouseUp,
            previewView: true,
          },
        }}
        className="w-full sm:w-[80%] md:w-[60%] lg:w-[40%] mx-auto"
        style={{ maxWidth: '90%', padding: '20px' }}
      />
    </div>
  );
};

export default ImageSelector;
